import { useState, useEffect } from 'react'

interface DocumentationContent {
  content: string
  lastModified?: string
  loading: boolean
  error: string | null
}

export function useDocumentationContent(): DocumentationContent {
  const [content, setContent] = useState<string>('')
  const [lastModified, setLastModified] = useState<string>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true)
        setError(null)
        
        console.log('[Documentation] Fetching from API route...')
        const response = await fetch('/api/documentation/content')
        
        if (!response.ok) {
          console.log('[Documentation] API failed with status:', response.status)
          throw new Error(`Failed to load documentation: ${response.statusText}`)
        }
        
        const data = await response.json()
        
        if (data.error) {
          console.log('[Documentation] API returned error:', data.error)
          throw new Error(data.error)
        }
        
        console.log('[Documentation] Successfully loaded from API, content length:', data.content?.length)
        setContent(data.content)
        setLastModified(data.lastModified)
      } catch (err) {
        console.error('[Documentation] Error fetching from API, using fallback:', err)
        setError(err instanceof Error ? err.message : 'Failed to load documentation')
        
        // Fallback to hardcoded content if API fails
        try {
          const { default: fallbackContent } = await import('@/data/training-guide')
          console.log('[Documentation] Using fallback content, length:', fallbackContent?.length)
          setContent(fallbackContent)
        } catch {
          // If even fallback fails, set empty content
          console.log('[Documentation] Fallback also failed, using empty content')
          setContent('')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchContent()
  }, [])

  return { content, lastModified, loading, error }
}